import React, { useState, useEffect } from 'react';
import { Box, LinearProgress } from '@mui/material';
import { TrendingUp, Timer, DirectionsRun } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { TFunction } from 'i18next';
import { Team } from '../../models/types';
import { apiService } from '../../services/api.service';
import { SegmentedControl } from '../ui/SegmentedControl';
import { TeamStatsBarRow } from './TeamStatsBarRow';

interface TeamStatsBarChartProps {
  teams: Team[];
  contestId: string;
  membersLocked: boolean;
  onAddMember: (team: Team) => void;
}

interface BackendTeamStatsResult {
  teamId: string;
  teamName: string;
  totalDistance: number;
  averagePace: number;
  totalTracklog: number;
  fastestPace: number;
  maxDistance: number;
  totalActivities: number;
  memberCount: number;
  rank: number;
  badge?: 'gold' | 'silver' | 'bronze';
  // Admin only: flagged activities not yet dismissed.
  suspiciousActivityCount?: number;
}

type MetricKey = 'totalDistance' | 'averagePace' | 'totalTracklog';

const buildMetricOptions = (t: TFunction): { label: string; value: MetricKey; icon: React.ReactNode; format: (v: number) => string }[] => [
  { label: t('teamStats.metrics.distance'), value: 'totalDistance', icon: <DirectionsRun />, format: v => t('common:units.km', { value: v.toFixed(1) }) },
  {
    label: t('teamStats.metrics.avgPace'),
    value: 'averagePace',
    icon: <Timer />,
    format: v => (v > 0 ? t('rules.paceValue', { value: v.toFixed(1) }) : t('teamStats.notAvailable')),
  },
  { label: t('teamStats.metrics.trackDays'), value: 'totalTracklog', icon: <TrendingUp />, format: v => t('teamStats.trackDays', { count: v }) },
];

export const TeamStatsBarChart: React.FC<TeamStatsBarChartProps> = ({ teams, contestId, membersLocked, onAddMember }) => {
  const { t } = useTranslation('contests');
  const metricOptions = buildMetricOptions(t);
  const [selectedMetric, setSelectedMetric] = useState<MetricKey>('totalDistance');
  const [backendTeamStats, setBackendTeamStats] = useState<BackendTeamStatsResult[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchTeamLeaderboard = async () => {
      try {
        setLoading(true);
        const leaderboard = await apiService.getContestTeamLeaderboard(contestId, selectedMetric, 1000);
        // Backend already returns the teams sorted and ranked.
        setBackendTeamStats(leaderboard.teams);
      } catch (error) {
        console.error('Error fetching team leaderboard:', error);
        // Fall back to zeroed stats so the team names still show.
        setBackendTeamStats(
          teams.map((team, index) => ({
            teamId: team._id,
            teamName: team.name,
            totalDistance: 0,
            averagePace: 0,
            totalTracklog: 0,
            fastestPace: 0,
            maxDistance: 0,
            totalActivities: 0,
            memberCount: team.numberOfMember || 0,
            rank: index + 1,
          }))
        );
      } finally {
        setLoading(false);
      }
    };

    fetchTeamLeaderboard();
  }, [contestId, selectedMetric, teams]);

  const metric = metricOptions.find(opt => opt.value === selectedMetric)!;
  const valueOf = (team: BackendTeamStatsResult) => Number(team[selectedMetric]) || 0;

  // Scale to the largest value; zero pace means "no data", so it never stretches the scale.
  const maxValue = Math.max(...backendTeamStats.map(valueOf).filter(v => selectedMetric !== 'averagePace' || v > 0), 1);

  return (
    <Box sx={{ p: '16px', display: 'flex', flexDirection: 'column', gap: 2 }}>
      <SegmentedControl size="small" ariaLabel={t('teamStats.metricLabel')} options={metricOptions.map(({ label, value, icon }) => ({ label, value, icon }))} value={selectedMetric} onChange={setSelectedMetric} />

      {loading ? (
        <LinearProgress />
      ) : (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {backendTeamStats.map((team, index) => {
            const value = valueOf(team);
            const noData = selectedMetric === 'averagePace' && value <= 0;
            return (
              <TeamStatsBarRow
                key={team.teamId}
                teamId={team.teamId}
                teamName={team.teamName}
                memberCount={team.memberCount}
                rank={team.rank}
                suspiciousCount={team.suspiciousActivityCount}
                valueLabel={metric.format(value)}
                widthPercent={noData ? 0 : (value / maxValue) * 100}
                highlight={index === 0}
                team={teams.find(candidate => candidate._id === team.teamId)}
                membersLocked={membersLocked}
                onAddMember={onAddMember}
              />
            );
          })}
        </Box>
      )}
    </Box>
  );
};
